import { captureException } from '@sentry/react';
import { classifyHttpError, isCustomError, UnknownError } from 'errors';
import isEmpty from 'lodash/isEmpty';
import take from 'lodash/take';
import { parse } from 'query-string';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { trackEvent } from 'utils';

import '../public/scss/common.scss';

import { Banner } from './Banner';
import { FolderbleButton } from './components/news-summary/FolderbleButton';
import { NewsSummary } from './components/news-summary/NewsSummary';
import { buildNewsApi, buildNewsSummaryApi } from './constants';
import { ScoreTypesProvider } from './contexts/scoreTypes';
import { Empty } from './Empty';
import { useSummaryToggle } from './hooks/useSummaryToggle';
import { NewsBody } from './NewsBody';
import { RelatedStock } from './RelatedStock';
import { Title } from './Title';
import { BannerOs } from './types';
import { request } from './utils/request';

const MAX_LENGTH_OF_SHORTEN_CODES = 5;

export const NewsIdContext = createContext(null);

interface NewsData {
  id: number;
  title: string;
  body: string;
  companyName: string;
  reporterName: string;
  createTime: string;
  updateTime: string;
  shortenCodes: string[];
  thumbnailUrl: string;
}
interface NewsSummaryData {
  summary?: string[];
}

const fontSizeClassName: { [key: number]: string } = {
  1: 'font01',
  2: 'font02',
  3: 'font03',
  4: 'font04',
  5: 'font05',
};

export const News = () => {
  const parsedSearch = parse(location.search);
  const newsId = parsedSearch?.news_id;
  const os = (parsedSearch?.os as BannerOs) ?? undefined;
  const [newsData, setNewsData] = useState<NewsData>();
  const [newsSummaryData, setNewsSummaryData] = useState<NewsSummaryData>();
  const [newsDataLoading, setNewsDataLoading] = useState(true);
  const [fontSize, setFontSize] = useState(1);

  // 뉴스 요약
  const { value: isCheckedSummary, getShowSummary, toggleSummary } = useSummaryToggle();
  const [isFolded, setIsFolded] = useState(true);
  const hasSummary = useMemo(() => newsSummaryData?.summary?.length > 0, [newsSummaryData]);

  useEffect(() => {
    if (isCheckedSummary) {
      setIsFolded(true);
    }
  }, [isCheckedSummary]);

  // 뉴스 api 조회
  useEffect(() => {
    // 뉴스 상세 api
    if (newsId && typeof newsId === 'string') {
      request({
        method: 'GET',
        url: buildNewsApi(newsId),
      })
        .then((r) => {
          setNewsDataLoading(false);
          if (r?.data?.status === 'success') {
            setNewsData(r?.data?.data?.news);
          }
        })
        .catch((error) => {
          setNewsDataLoading(false);
          setNewsData(undefined);

          let customError: unknown;

          const classifiedError = classifyHttpError(error);
          if (isCustomError(classifiedError)) {
            customError = classifiedError;
          } else {
            customError = new UnknownError(error);
          }

          captureException(customError);
        });
    }

    // 뉴스 요약 api
    if (newsId && typeof newsId === 'string') {
      request({
        method: 'GET',
        url: buildNewsSummaryApi(newsId),
      })
        .then((r) => {
          if (r?.data?.status === 'success') {
            setNewsSummaryData(r?.data?.data?.news);
          }
        })
        .catch((error) => {
          let customError: unknown;

          const classifiedError = classifyHttpError(error);
          if (isCustomError(classifiedError)) {
            customError = classifiedError;
          } else {
            customError = new UnknownError(error);
          }

          captureException(customError);
        });
    }
  }, [newsId]);

  useEffect(() => {
    const isOriginCheckedSummary = getShowSummary();
    trackEvent('화면진입_뉴스상세', {
      page: '뉴스상세_계약',
      eventLabel_1: `${isOriginCheckedSummary ? 'ON' : 'OFF'}`,
      eventDetail_1: `${newsId}`,
    });
  }, [newsId]);

  const { title, body, companyName, reporterName, createTime, updateTime, shortenCodes, thumbnailUrl } = newsData || {};
  const takenCodes = useMemo(() => {
    return shortenCodes ? take(shortenCodes, MAX_LENGTH_OF_SHORTEN_CODES) : [];
  }, [shortenCodes]);

  if (!newsId) {
    return <Empty />;
  }

  const handleFolderbleClick = (isFolded: boolean) => {
    setIsFolded(isFolded);
    trackEvent('더보기/상세보기_클릭', {
      page: '뉴스상세_계약',
      eventLabel_1: `${isFolded ? '뉴스 원문 접기' : '뉴스 원문 읽기'}`,
      eventDetail_1: `${newsId}`,
    });
  };

  return !isEmpty(newsData) ? (
    <NewsIdContext.Provider value={newsId}>
      <Helmet>
        <meta property="og:site_name" content="증권플러스 뉴스" />
        <meta property="og:title" content={title} />
        <meta property="og:image" content={thumbnailUrl} />
        <meta property="og:description" content={title} />
      </Helmet>
      <main className="mainB">
        <section className="contents">
          <article className={`articleB pt28 ${fontSizeClassName[fontSize] ?? ''}`}>
            <Title
              title={title}
              companyName={companyName}
              reporterName={reporterName}
              createTime={createTime}
              updateTime={updateTime}
              fontSize={fontSize}
              setFontSize={setFontSize}
            />
            <NewsSummary
              newsId={newsId}
              summaryBody={newsSummaryData?.summary}
              thumbnailUrl={newsData?.thumbnailUrl}
              isCheckedSummary={isCheckedSummary}
              toggleSummary={toggleSummary}
            />

            {isCheckedSummary && hasSummary && isFolded && (
              <FolderbleButton onClick={() => handleFolderbleClick(false)}>
                뉴스 원문 읽기 <i className="icoArrow "></i>
              </FolderbleButton>
            )}

            <NewsBody
              body={body}
              isFolded={isFolded}
              isCheckedSummary={isCheckedSummary}
              summaryBody={newsSummaryData?.summary}
            />

            {isCheckedSummary && hasSummary && !isFolded && (
              <FolderbleButton onClick={() => handleFolderbleClick(true)}>
                뉴스 원문 접기 <i className="icoArrow"></i>
              </FolderbleButton>
            )}
          </article>
          <Banner
            type="ty02"
            screenKey="news_detail_bottom"
            os={os}
            adsenseSlot="6364056231"
            adsenseKey="news_adsense_bottom"
          />
          <ScoreTypesProvider>
            <RelatedStock shortenCodes={takenCodes} os={os} />
          </ScoreTypesProvider>
        </section>
      </main>
    </NewsIdContext.Provider>
  ) : (
    !newsDataLoading && <Empty />
  );
};
